import { Button, Flex, Text, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { auth } from "../../../core/auth";
// TODO(FC-GATE): restore with the block in the component body before deploying.
// import { FC_AGENCY_ID } from "../../../shared/consts";
import useApi from "../../../shared/hooks/useApi";
import { PatientId } from "../../../shared/schema/schema";
import AiWeeklyTemplateBuilderModal from "./AiWeeklyTemplateBuilderModal";

const BUTTON_LABEL = "Weekly template assistant";

interface Props {
  patientId: PatientId;
}

/**
 * The entry point mounted into the AngularJS "Weekly template" section — the
 * screen the coordinator is already on when they need this.
 *
 * The section itself is gated on `view_patient_page_weekly_template` by the
 * patient page's section registry, so this only has to add the second
 * permission the API enforces.
 */
export default function AiWeeklyTemplateBuilderSection(props: Props) {
  const disclosure = useDisclosure();
  const { queries } = useApi();

  // Only to decide between the prompt and the plain button — the template table
  // itself is rendered by the AngularJS component below this one.
  const existingTemplate = useQuery(
    queries.aiWeeklyTemplateBuilder.existingTemplate(props.patientId)
  );

  // TODO(FC-GATE): DISABLED FOR TESTING — every agency can currently see this.
  // Restore before deploying, together with the FC_AGENCY_ID import above. This
  // limits the feature to FreedomCare while it beds in, matching how the
  // Authorization Utilization report was rolled out.
  //
  // const isFCAgency = auth.getAuthOrFail().data.agency.id === FC_AGENCY_ID;
  // if (!isFCAgency) {
  //   return null;
  // }

  if (!auth.isAllowedTo("view_authorization_utilization")) {
    return null;
  }

  const modal = disclosure.isOpen ? (
    <AiWeeklyTemplateBuilderModal patientId={props.patientId} onClose={disclosure.onClose} />
  ) : null;

  // An empty table on its own tells a coordinator nothing about what to do next.
  const hasNoTemplate = existingTemplate.status === "success" && existingTemplate.data.length === 0;

  const content = hasNoTemplate ? (
    <Flex
      alignItems="center"
      bg="gray.50"
      borderRadius="md"
      direction="column"
      gap={3}
      mb={4}
      py={8}
    >
      <Text fontWeight="semibold">No weekly template yet — let&apos;s build one.</Text>
      <Button colorScheme="blue" onClick={disclosure.onOpen}>
        {BUTTON_LABEL}
      </Button>
    </Flex>
  ) : (
    <Flex justifyContent="flex-end" pb={2}>
      <Button colorScheme="blue" size="sm" variant="outline" onClick={disclosure.onOpen}>
        {BUTTON_LABEL}
      </Button>
    </Flex>
  );

  // The modal is rendered outside the branch, at a fixed position with a stable
  // key. Creating a template flips `hasNoTemplate` *while the modal is open*, and
  // React matches unkeyed children by position — with the modal inside the
  // branches it changed slot, remounted, and threw away the coordinator's state
  // (including the result they had just created) at the exact moment it
  // succeeded.
  return (
    <>
      {content}
      <React.Fragment key="ai-weekly-template-builder-modal">{modal}</React.Fragment>
    </>
  );
}
